import React, { useState } from "react";

import { Modal, MoreIcon, Typography } from "@packages/ds";
import moment from "moment";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import Button, { ButtonWrapper } from "ds/Button";
import * as Dropdown from "ds/Dropdown";
import * as Sticker from "ds/Sticker";
import * as Table from "ds/Table";
import Tag from "ds/Tag";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { capitalize } from "Libs/utils";
import { environmentSelector } from "Reducers/environment";
import { gitProjectSelector } from "Reducers/project/project";
import { useLoadDomain } from "Reducers/project/settings/domains/hooks/useLoadDomain";
import { useDeleteEnvironmentDomain } from "Reducers/project/settings/environmentDomains/hooks/useDeleteEnvironmentDomain";
import ConfigureDomainInfo from "src/organization/pages/project/environment/settings/domains/components/ConfigureDomainInfo/ConfigureDomainInfo";
import ModalConfirmDelete from "src/organization/pages/project/settings/domains/components/DeleteModal";

import { StickerRoot, StyledTable, BodyText, DropdownTrigger } from "./styles";

import type { EnvironmentDomain, Domain } from "@packages/client";

type EnvironmentDomainsListProps = {
  environmentDomains: EnvironmentDomain[] | Domain[];
  userHasPermissions?: boolean;
};

const EnvironmentDomainsList = ({
  environmentDomains,
  userHasPermissions
}: EnvironmentDomainsListProps) => {
  const intl = useIntl();
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [sort, setSort] = Table.useSort<{
    domain: string;
    date: string;
  }>({
    key: "domain",
    direction: "asc"
  });
  const [domainDelete, setDomainDelete] = useState<any>(null);

  const navigate = useNavigate();

  const project = useSelectorWithUrlParams(gitProjectSelector);

  const environment = useSelectorWithUrlParams(environmentSelector);

  const { environmentId, organizationId, projectId } = useDecodedParams<{
    environmentId: string;
    organizationId: string;
    projectId: string;
  }>();

  const encodedEnvironmentId = encodeURIComponent(environmentId);

  const isProductionEnvironment = environment?.type === "production";

  const viewDomain = domain => {
    navigate(
      `/${organizationId}/${projectId}/${encodedEnvironmentId}/settings/domains/${domain}`
    );
  };
  const { handleDelete: handleDeleteEnvironmentDomain } =
    useDeleteEnvironmentDomain(project, environment);

  const { handleDelete } = useLoadDomain(project);

  const sortEnvironments = (
    environmentDomains: (EnvironmentDomain | Domain)[]
  ) => {
    const sortedEnvironments = [...environmentDomains];

    return sortedEnvironments.sort((a, b) => {
      if (sort.key === "domain") {
        if (a.name < b.name) {
          return sort.direction === "asc" ? -1 : 1;
        } else if (a.name > b.name) {
          return sort.direction === "asc" ? 1 : -1;
        }
        return 0;
      }

      const aCreatedAt = new Date(a.created_at).getTime();
      const bCreatedAt = new Date(b.created_at).getTime();
      if (aCreatedAt < bCreatedAt) {
        return sort.direction === "asc" ? -1 : 1;
      } else if (aCreatedAt > bCreatedAt) {
        return sort.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  const stickerText = () => {
    if (isProductionEnvironment) {
      return "settings.domains.sticker.no_permissions_prod_domain";
    } else {
      return "settings.domains.sticker.no_permissions";
    }
  };

  const closeModals = () => {
    setShowDeleteModal(false);
  };

  return (
    <div>
      {!userHasPermissions && (
        <StickerRoot priority="information">
          <Sticker.Icon priority="information" />
          <Sticker.Text textStyle="information">
            {intl.formatMessage({
              id: stickerText()
            })}
          </Sticker.Text>
        </StickerRoot>
      )}

      <ConfigureDomainInfo closed />

      <StyledTable>
        <Table.Thead>
          <Table.Th
            width="75%"
            onSortChanged={() => {
              setSort("domain");
            }}
            isSortActive={sort.key === "domain"}
            sortDirection={sort.direction}
          >
            {capitalize(intl.formatMessage({ id: "domain" }))}
          </Table.Th>
          <Table.Th
            width="20%"
            onSortChanged={() => {
              setSort("date");
            }}
            isSortActive={sort.key === "date"}
            sortDirection={sort.direction}
          >
            {capitalize(intl.formatMessage({ id: "added" }))}
          </Table.Th>
        </Table.Thead>
        <Table.Tbody>
          {sortEnvironments(environmentDomains)?.map(domain => (
            <Table.Tr key={domain?.id}>
              <Table.Td width="75%">
                <BodyText weight="bold">
                  {domain.name}{" "}
                  {project?.default_domain === domain.name && (
                    <Tag variant="purple">
                      {capitalize(intl.formatMessage({ id: "default" }))}
                    </Tag>
                  )}
                </BodyText>
              </Table.Td>
              <Table.Td width="">
                <Typography.BodyText size="extraSmall" weight="semiBold">
                  {moment(domain?.created_at).format("MMMM D, YYYY")}
                </Typography.BodyText>
              </Table.Td>
              <Table.Td width="5%">
                <Dropdown.Root id="environment_domain">
                  <DropdownTrigger>
                    <MoreIcon />
                  </DropdownTrigger>
                  <Dropdown.Portal>
                    <Dropdown.Content>
                      <Dropdown.Item
                        onClick={() => {
                          viewDomain(domain.id);
                        }}
                      >
                        {capitalize(intl.formatMessage({ id: "view" }))}
                      </Dropdown.Item>
                      {userHasPermissions && (
                        <Dropdown.Item
                          onClick={() => {
                            setDomainDelete(domain);
                            setShowDeleteModal(true);
                          }}
                        >
                          {capitalize(intl.formatMessage({ id: "delete" }))}
                        </Dropdown.Item>
                      )}
                    </Dropdown.Content>
                  </Dropdown.Portal>
                </Dropdown.Root>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </StyledTable>

      {isProductionEnvironment ? (
        <ModalConfirmDelete
          closeModal={closeModals}
          deleteEnvironmentDomain={handleDelete}
          domain={domainDelete}
          isOpen={showDeleteModal}
          showingWarning={
            environmentDomains?.length !== 0 && isProductionEnvironment
          }
        />
      ) : (
        <Modal
          isOpen={showDeleteModal}
          onOpenChange={closeModals}
          handleClose={closeModals}
          id="delete-domain-modal"
        >
          <Typography.Title tag="h2">
            {intl.formatMessage({
              id: "settings.domains.modal.delete.title"
            })}
          </Typography.Title>
          <Typography.BodyText>
            {intl.formatMessage(
              {
                id: "settings.domains.delete.domain"
              },
              {
                domainName: <strong>{domainDelete?.name}</strong>
              }
            )}
          </Typography.BodyText>
          <ButtonWrapper spacing="modal" justifyContent="end">
            <Button
              variant="secondary"
              analyticId="cancel"
              onClick={closeModals}
            >
              {intl.formatMessage({ id: "cancel" })}
            </Button>
            <Button
              id="delete-domain-confirm-modal-button"
              analyticId="settings.domains.modal.delete.confirm"
              onClick={() => {
                if (domainDelete) {
                  handleDeleteEnvironmentDomain(domainDelete);
                }

                closeModals();
              }}
            >
              {intl.formatMessage({
                id: "settings.domains.modal.delete.confirm"
              })}
            </Button>
          </ButtonWrapper>
        </Modal>
      )}
    </div>
  );
};
export default EnvironmentDomainsList;
